import React, { useState, useMemo } from 'react';
import { calculateCiphers } from './ciphers';
import { findResonances } from './resonance';
import { useAudio } from '../../audio/useAudio';

interface AnvilOfSolomonProps {
  initialInput?: string;
  onSendToOracle?: (legionId: number) => void;
}

const PRESET_PHRASES = [
  { label: 'KING BELIAL', text: 'BELIAL' },
  { label: 'HEBREW בְּלִיַּעַל', text: 'בליעל' },
  { label: 'DUKANTE ENN', text: 'Lirach Tasa Vefa Wehlc Belial' },
  { label: "BELI-'OL (Without Yoke)", text: 'BELI OL' },
  { label: 'SINE DOMINO', text: 'SINE DOMINO' },
  { label: 'SOLI INVICTO', text: 'SOLI INVICTO' },
  { label: "DA'ATH", text: 'DAATH' }
];

export const AnvilOfSolomon: React.FC<AnvilOfSolomonProps> = ({ initialInput = 'BELIAL', onSendToOracle }) => {
  const [inputText, setInputText] = useState<string>(initialInput);
  const [satanicMode, setSatanicMode] = useState<boolean>(false);
  const [activeCipherBreakdown, setActiveCipherBreakdown] = useState<string | null>(null);
  const { playAnvilStrike } = useAudio();

  const calculation = useMemo(() => {
    return calculateCiphers(inputText, satanicMode);
  }, [inputText, satanicMode]);

  // Derive primary resonance from the dominant cipher (Hebrew or ALW or Agrippa)
  const resonances = useMemo(() => {
    const dominantSum = calculation.hebrew.value || calculation.englishALW.value || calculation.agrippa.value || calculation.dukante.value;
    return findResonances(dominantSum);
  }, [calculation]);

  const handleStrike = () => {
    playAnvilStrike();
  };

  return (
    <div className="space-y-8 animate-fade-in" id="anvil-of-solomon-container">
      {/* Header */}
      <div className="border-b border-iron-dark pb-6">
        <div className="inline-flex items-center gap-2 rounded border border-bronze-dark/80 bg-obsidian-deep/80 px-3 py-1 text-[11px] font-display tracking-widest text-gold-ancient uppercase mb-2">
          <span>INCUDO NUMERORUM</span>
          <span>•</span>
          <span>5-SYSTEM CIPHER ENGINE</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-parchment-light uppercase mt-1">
              The Anvil of Solomon
            </h1>
            <p className="text-xs sm:text-sm text-parchment-dim mt-1 max-w-2xl font-serif">
              Multi-system gematria and alphanumeric cipher engine executing instant cross-system calculations with Goetic & Qliphothic resonance matching.
            </p>
          </div>
          <button
            onClick={handleStrike}
            aria-label="Strike Anvil SFX"
            className="btn-gold px-5 py-2.5 text-xs font-semibold rounded flex items-center gap-2 self-start sm:self-auto shadow-glow-bronze"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18h12M4 6h16M7 6v12M17 6v12" />
            </svg>
            Strike Anvil SFX
          </button>
        </div>
      </div>

      {/* Input Section & Presets */}
      <div className="chthonic-card rounded-lg p-6 sm:p-8 space-y-6">
        <div className="space-y-2">
          <label htmlFor="gematria-input" className="block text-xs font-mono uppercase tracking-widest text-gold-bright">
            Inscribe Phrase, Name, or Formula (Hebrew, Greek, or Latin Alphanumeric)
          </label>
          <div className="relative">
            <input
              id="gematria-input"
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="e.g. BELIAL, בליעל, LIRACH TASA VEFA..."
              className="w-full rounded border border-iron-base bg-obsidian-deep px-4 py-3 text-base sm:text-lg font-mono text-parchment-light placeholder-iron-muted focus:border-gold-bright focus:outline-none focus:ring-1 focus:ring-gold-bright transition-all"
            />
            {inputText && (
              <button
                onClick={() => setInputText('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-iron-steel hover:text-parchment-light text-xs font-mono px-2 py-1"
                aria-label="Clear input"
              >
                CLEAR
              </button>
            )}
          </div>
        </div>

        {/* Presets Bar */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="text-[10px] font-mono text-iron-steel uppercase mr-1">PRESETS:</span>
          {PRESET_PHRASES.map((preset) => (
            <button
              key={preset.text}
              onClick={() => {
                setInputText(preset.text);
                playAnvilStrike();
              }}
              className="rounded border border-iron-dark bg-obsidian-surface/60 px-2.5 py-1 text-[11px] font-mono text-parchment-dim hover:border-gold-ancient hover:text-gold-bright transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center gap-3 pt-2 border-t border-iron-dark/60">
          <span className="text-xs font-mono text-iron-steel uppercase">DUKANTE CIPHER MODE:</span>
          <button
            onClick={() => setSatanicMode(!satanicMode)}
            className={`px-3 py-1 text-xs font-mono rounded transition-colors ${
              satanicMode
                ? 'bg-ember-dark text-ember-spark border border-ember-flare font-semibold'
                : 'border border-iron-base bg-obsidian-surface text-parchment-dim hover:text-parchment-light'
            }`}
          >
            {satanicMode ? 'Satanic Shift (Ordinal+35 / 666)' : 'Nine Demonic Gates (Standard)'}
          </button>
        </div>
      </div>

      {/* 5-System Gematria Display Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* 1. Hebrew */}
        <div 
          onClick={() => setActiveCipherBreakdown(activeCipherBreakdown === 'hebrew' ? null : 'hebrew')}
          className={`cursor-pointer chthonic-card rounded-lg p-5 space-y-2 transition-all ${
            activeCipherBreakdown === 'hebrew' ? 'border-gold-bright shadow-glow-gold' : ''
          }`}
        >
          <div className="flex justify-between items-center">
            <span className="text-[10px] font-mono text-gold-ancient uppercase">HEBREW GEMATRIA</span>
            <span className="text-[10px] font-mono text-iron-steel">MISPAR</span>
          </div>
          <div className="text-3xl sm:text-4xl font-display font-bold text-gold-bright my-1">
            {calculation.hebrew.value}
          </div>
          <div className="flex justify-between text-[11px] font-mono text-parchment-dim">
            <span>Root: <strong className="text-gold-ancient">{calculation.hebrew.reduction}</strong></span>
            <span>Gadol: <strong className="text-parchment-light">{calculation.hebrew.gadol}</strong></span>
          </div>
          <div className="text-[9px] font-mono text-iron-steel pt-1 border-t border-iron-dark/50">
            Click to inspect letters ({calculation.hebrew.breakdown.length})
          </div>
        </div>

        {/* 2. Greek */}
        <div 
          onClick={() => setActiveCipherBreakdown(activeCipherBreakdown === 'greek' ? null : 'greek')}
          className={`cursor-pointer chthonic-card rounded-lg p-5 space-y-2 transition-all ${
            activeCipherBreakdown === 'greek' ? 'border-gold-bright shadow-glow-gold' : ''
          }`}
        >
          <div className="flex justify-between items-center">
            <span className="text-[10px] font-mono text-gold-ancient uppercase">GREEK ISOPSEPHY</span>
            <span className="text-[10px] font-mono text-iron-steel">MILESIAN</span>
          </div>
          <div className="text-3xl sm:text-4xl font-display font-bold text-gold-bright my-1">
            {calculation.greek.value}
          </div>
          <div className="flex justify-between text-[11px] font-mono text-parchment-dim">
            <span>Root: <strong className="text-gold-ancient">{calculation.greek.reduction}</strong></span>
            <span>Chars: <strong className="text-parchment-light">{calculation.greek.breakdown.length}</strong></span>
          </div>
          <div className="text-[9px] font-mono text-iron-steel pt-1 border-t border-iron-dark/50">
            Click to inspect letters ({calculation.greek.breakdown.length})
          </div>
        </div>

        {/* 3. Agrippa */}
        <div 
          onClick={() => setActiveCipherBreakdown(activeCipherBreakdown === 'agrippa' ? null : 'agrippa')}
          className={`cursor-pointer chthonic-card rounded-lg p-5 space-y-2 transition-all ${
            activeCipherBreakdown === 'agrippa' ? 'border-gold-bright shadow-glow-gold' : ''
          }`}
        >
          <div className="flex justify-between items-center">
            <span className="text-[10px] font-mono text-gold-ancient uppercase">AGRIPPAN LATIN</span>
            <span className="text-[10px] font-mono text-iron-steel">1533 CE</span>
          </div>
          <div className="text-3xl sm:text-4xl font-display font-bold text-gold-bright my-1">
            {calculation.agrippa.value}
          </div>
          <div className="flex justify-between text-[11px] font-mono text-parchment-dim">
            <span>Root: <strong className="text-gold-ancient">{calculation.agrippa.reduction}</strong></span>
            <span>Scale: <strong className="text-parchment-light">3 Tiers</strong></span>
          </div>
          <div className="text-[9px] font-mono text-iron-steel pt-1 border-t border-iron-dark/50">
            Click to inspect letters ({calculation.agrippa.breakdown.length})
          </div>
        </div>

        {/* 4. English ALW */}
        <div 
          onClick={() => setActiveCipherBreakdown(activeCipherBreakdown === 'englishALW' ? null : 'englishALW')}
          className={`cursor-pointer chthonic-card rounded-lg p-5 space-y-2 transition-all ${
            activeCipherBreakdown === 'englishALW' ? 'border-gold-bright shadow-glow-gold' : ''
          }`}
        >
          <div className="flex justify-between items-center">
            <span className="text-[10px] font-mono text-gold-ancient uppercase">ENGLISH ALW</span>
            <span className="text-[10px] font-mono text-iron-steel">KEY 11</span>
          </div>
          <div className="text-3xl sm:text-4xl font-display font-bold text-gold-bright my-1">
            {calculation.englishALW.value}
          </div>
          <div className="flex justify-between text-[11px] font-mono text-parchment-dim">
            <span>Root: <strong className="text-gold-ancient">{calculation.englishALW.reduction}</strong></span>
            <span>Mod: <strong className="text-parchment-light">11×N mod 26</strong></span>
          </div>
          <div className="text-[9px] font-mono text-iron-steel pt-1 border-t border-iron-dark/50">
            Click to inspect letters ({calculation.englishALW.breakdown.length})
          </div>
        </div>

        {/* 5. Dukante */}
        <div 
          onClick={() => setActiveCipherBreakdown(activeCipherBreakdown === 'dukante' ? null : 'dukante')}
          className={`cursor-pointer chthonic-card rounded-lg p-5 space-y-2 transition-all ${
            activeCipherBreakdown === 'dukante' ? 'border-ember-flare shadow-glow-ember' : ''
          }`}
        >
          <div className="flex justify-between items-center">
            <span className="text-[10px] font-mono text-ember-spark uppercase">
              {satanicMode ? 'SATANIC (666)' : 'DUKANTE CIPHER'}
            </span>
            <span className="text-[10px] font-mono text-iron-steel">GATE {calculation.dukante.gate}</span>
          </div>
          <div className="text-3xl sm:text-4xl font-display font-bold text-ember-flare my-1">
            {calculation.dukante.value}
          </div>
          <div className="flex justify-between text-[11px] font-mono text-parchment-dim truncate">
            <span>Ruler: <strong className="text-ember-spark">{calculation.dukante.primaryDemon}</strong></span>
            <span>Root: <strong className="text-gold-ancient">{calculation.dukante.reduction}</strong></span>
          </div>
          <div className="text-[9px] font-mono text-iron-steel pt-1 border-t border-iron-dark/50">
            Click to inspect letters ({calculation.dukante.breakdown.length})
          </div>
        </div>
      </div>

      {/* Letter-by-Letter Breakdown Drawer */}
      {activeCipherBreakdown && (
        <div className="rounded-lg border border-iron-dark bg-obsidian-surface/90 p-5 space-y-3 animate-fade-in">
          <div className="flex justify-between items-center border-b border-iron-dark/60 pb-2">
            <span className="text-xs font-mono text-gold-bright uppercase tracking-wider">
              {activeCipherBreakdown.toUpperCase()} CHARACTER BREAKDOWN
            </span>
            <button
              onClick={() => setActiveCipherBreakdown(null)}
              className="text-xs font-mono text-iron-steel hover:text-parchment-light"
            >
              CLOSE [×]
            </button>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            {calculation[activeCipherBreakdown as keyof typeof calculation] && 
             Array.isArray((calculation[activeCipherBreakdown as keyof typeof calculation] as { breakdown: { char: string; val: number }[] }).breakdown) && 
             (calculation[activeCipherBreakdown as keyof typeof calculation] as { breakdown: { char: string; val: number }[] }).breakdown.map((item, idx) => (
              <div key={idx} className="flex flex-col items-center justify-center rounded border border-iron-base bg-obsidian-deep px-3 py-1.5 min-w-[42px]">
                <span className="font-mono text-xs font-bold text-parchment-light">{item.char}</span>
                <span className="font-mono text-[10px] text-gold-bright">{item.val}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Goetic & Qliphothic Resonance Matching Section */}
      <div className="chthonic-card rounded-lg p-6 sm:p-8 space-y-6">
        <div className="border-b border-iron-dark pb-4">
          <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-widest">
            HARMONIA SPHAERARUM • GOETIC & QLIPHOTHIC RESONANCE
          </span>
          <h3 className="font-display text-2xl text-parchment-light mt-1">
            Resonance Alignment Matrix
          </h3>
          <p className="text-xs text-parchment-dim mt-1 font-serif">
            Mathematical harmonies detected against the 72 Goetic spirits, the 80 Legions of King Belial, and the 10 Qliphothic Spheres.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {resonances.map((match, idx) => (
            <div
              key={idx}
              className={`rounded-lg border p-5 space-y-3 transition-all ${
                match.harmonicLock
                  ? 'border-gold-bright bg-obsidian-surface shadow-glow-gold'
                  : 'border-iron-dark bg-obsidian-deep/80'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-mono text-gold-ancient uppercase">
                  #{match.goeticNumber} • {match.rank}
                </span>
                {match.harmonicLock && (
                  <span className="inline-flex items-center gap-1 rounded bg-gold-ancient/20 border border-gold-bright/60 px-2 py-0.5 text-[9px] font-mono text-gold-bright uppercase tracking-wider">
                    HARMONIC LOCK
                  </span>
                )}
              </div>

              <h4 className="font-display text-lg font-bold text-parchment-light">
                {match.goeticName}
              </h4>

              <div className="text-xs font-mono text-ember-spark">
                Sphere: <span className="text-parchment-base">{match.qliphothicSphere}</span>
              </div>

              <p className="text-xs text-parchment-dim font-serif leading-relaxed">
                {match.description}
              </p>

              <div className="pt-2 flex items-center justify-between border-t border-iron-dark/60 text-[10px] font-mono text-iron-steel">
                <span>Score: {Math.round(match.resonanceScore * 100)}%</span>
                {onSendToOracle && (
                  <button
                    onClick={() => onSendToOracle(match.goeticNumber)}
                    className="text-gold-bright hover:underline"
                  >
                    Send to Oracle →
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnvilOfSolomon;
